var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import Icon, { GLYPHS } from 'frontend/js/app/common/icon';
import { Button } from 'frontend/js/modules/infra/dls/components/button/button';
import { IconButton } from 'frontend/js/modules/infra/dls/components/icon-button/icon-button';
import React, { useCallback, useEffect } from 'react';
import { useBreakpoints } from '../../../infra/dls/hooks/use-get-breakpoints';
import styles from './styles.module.scss';
export var AIPopover = function (_a) {
    var _b, _c;
    var onClose = _a.onClose, onRephraseClick = _a.onRephraseClick, onMoreFormalClick = _a.onMoreFormalClick, onMorePlayfulClick = _a.onMorePlayfulClick, onShortenClick = _a.onShortenClick, onElaborateClick = _a.onElaborateClick, onPromptSubmit = _a.onPromptSubmit, _d = _a.isLoading, isLoading = _d === void 0 ? false : _d, _e = _a.isFetchingSuggestions, isFetchingSuggestions = _e === void 0 ? false : _e, currentAction = _a.currentAction, autoFocusPrompt = _a.autoFocusPrompt, _f = _a.dynamicSuggestions, dynamicSuggestions = _f === void 0 ? [] : _f, _g = _a.suggestionsAreDirectContent, suggestionsAreDirectContent = _g === void 0 ? false : _g, onInsertContent = _a.onInsertContent, onUndoClick = _a.onUndoClick, _h = _a.hasUndoContent, hasUndoContent = _h === void 0 ? false : _h, onGenerateSimilarClick = _a.onGenerateSimilarClick, _j = _a.type, type = _j === void 0 ? 'text' : _j, isEmpty = _a.isEmpty, _k = _a.isShortenAllowed, isShortenAllowed = _k === void 0 ? false : _k;
    var _l = React.useState(''), customPrompt = _l[0], setCustomPrompt = _l[1];
    var isMobileOrSmaller = useBreakpoints().isMobileOrSmaller;
    var promptInputRef = React.useRef(null);
    var popoverRef = React.useRef(null);
    var focusPrompt = useCallback(function () {
        var _a;
        if (autoFocusPrompt) {
            (_a = promptInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [autoFocusPrompt]);
    useEffect(function () {
        var _a;
        var checkIfClickedOutside = function () {
            focusPrompt();
        };
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('click', checkIfClickedOutside);
        return function () {
            var _a;
            (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', checkIfClickedOutside);
        };
    }, [focusPrompt]);
    var handleClose = function () {
        onClose();
    };
    var handlePromptChange = function (e) {
        setCustomPrompt(e.target.value);
    };
    var handlePromptSubmit = function () {
        if (customPrompt.trim()) {
            onPromptSubmit(customPrompt);
            setCustomPrompt('');
        }
    };
    var handleKeyDown = function (e) {
        if (e.key === 'Enter') {
            handlePromptSubmit();
        }
    };
    // Base hardcoded suggestions - for images, we only show these when there's an existing image
    var baseSuggestions = onGenerateSimilarClick
        ? [
            // For images: only show Generate Similar when there's an existing image
            {
                label: 'Generate similar',
                icon: GLYPHS.refresh,
                onClick: onGenerateSimilarClick,
            },
        ]
        : __spreadArray(__spreadArray([
            // For text: show the standard text editing options
            {
                label: 'Rephrase',
                icon: GLYPHS.refresh,
                onClick: onRephraseClick,
            },
            {
                label: 'More formal',
                icon: GLYPHS.edit,
                onClick: onMoreFormalClick,
            },
            {
                label: 'More playful',
                icon: GLYPHS.rewrite,
                onClick: onMorePlayfulClick,
            }
        ], (isShortenAllowed
            ? [
                {
                    label: 'Shorten',
                    icon: GLYPHS.textAlignMiddle,
                    onClick: onShortenClick,
                },
            ]
            : []), true), [
            {
                label: 'Elaborate',
                icon: GLYPHS.textParagraphSpacing,
                onClick: onElaborateClick,
            },
        ], false);
    // Use dynamic suggestions if available, otherwise fall back to hardcoded suggestions
    var suggestions = dynamicSuggestions.length > 0
        ? dynamicSuggestions.map(function (suggestion) { return ({
            label: suggestion.label,
            icon: GLYPHS.rewrite,
            onClick: function () {
                // Handle based on whether suggestions are direct content or prompts
                if (suggestionsAreDirectContent && onInsertContent) {
                    onInsertContent(suggestion.prompt); // Insert content directly
                }
                else {
                    onPromptSubmit(suggestion.prompt); // Send as refine prompt
                }
            },
        }); })
        : baseSuggestions;
    // Add undo button at the beginning if there's content to undo
    var allSuggestions = hasUndoContent && onUndoClick
        ? __spreadArray([
            {
                label: 'Undo',
                icon: GLYPHS.rename,
                iconSize: 'medium',
                onClick: onUndoClick,
            }
        ], suggestions, true) : suggestions;
    // Add Done button as first suggestion on mobile (after undo if present)
    var mobileSuggestions = __spreadArray([
        {
            label: 'Done',
            icon: GLYPHS.checkMark,
            iconSize: 'small',
            onClick: handleClose,
        }
    ], allSuggestions, true);
    var suggestionsToShow = (isMobileOrSmaller ? mobileSuggestions : allSuggestions);
    return (_jsxs("div", { ref: popoverRef, className: styles['suggestions-popover'], children: [!isMobileOrSmaller && (_jsxs("div", { className: styles['popover-header'], children: [_jsx("span", { className: styles['popover-title'], children: isLoading ? "Generating ".concat(type) : 'Suggestions' }), (isLoading || isFetchingSuggestions) && (_jsxs("div", { className: styles['loading-dots'], children: [_jsx("span", { className: styles.dot1 }), _jsx("span", { className: styles.dot2 }), _jsx("span", { className: styles.dot3 })] })), _jsx(IconButton.S, { Icon: _jsx(Icon, { glyph: GLYPHS.close, className: styles.closeButton }), onClick: handleClose, className: styles['close-button'] })] })), (function () {
                if (isLoading) {
                    return (_jsx("div", { className: styles['loading-content'], children: _jsxs("div", { className: styles['current-action'], children: ["\u201C", currentAction, "\u201D"] }) }));
                }
                if (isFetchingSuggestions) {
                    return (_jsx("div", { className: styles['skeleton-container'], children: [1, 2, 3, 4].map(function (item) { return (_jsx("div", { className: styles['skeleton-item'] }, "skeleton-".concat(item))); }) }));
                }
                return (_jsx("div", { className: styles['buttons-container'], children: _jsx("div", { className: styles['buttons-row'], children: suggestionsToShow.map(function (suggestion) { return (_jsx(Button.Secondary, { className: styles['suggestion-button'], onClick: suggestion.onClick, startIcon: _jsx(Icon, { glyph: suggestion.icon, size: suggestion.iconSize || 'medium' }), children: suggestion.label }, suggestion.label)); }) }) }));
            })(), _jsx("div", { className: styles['prompt-container'], children: isMobileOrSmaller ? (
                // Mobile prompt layout - simpler design
                _jsx("div", { className: styles['mobile-prompt-wrapper'], children: _jsxs("div", { className: styles['mobile-prompt-input-container'], children: [_jsx(Icon, { glyph: GLYPHS.aiIcon, size: "small", className: styles['mobile-prompt-icon'] }), _jsx("input", { type: "text", name: "prompt", ref: promptInputRef, className: styles['mobile-prompt-input'], placeholder: "".concat(isEmpty ? 'Generate' : 'Refine', " ").concat(type, " with a prompt"), value: customPrompt, onChange: handlePromptChange, onKeyDown: handleKeyDown, 
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus: autoFocusPrompt }), _jsx("button", { className: classNames(styles['mobile-submit-button'], (_b = {},
                                    _b[styles['is-focused']] = !!customPrompt,
                                    _b)), onClick: handlePromptSubmit, type: "button", children: _jsx(Icon, { glyph: GLYPHS.arrowUp }) })] }) })) : (
                // Desktop prompt layout - original design
                _jsxs("div", { className: styles['prompt-input-wrapper'], children: [_jsxs("div", { className: styles['prompt-input-container'], children: [_jsx("input", { type: "text", name: "prompt", className: styles['prompt-input'], placeholder: "".concat(isEmpty ? 'Generate' : 'Refine', " ").concat(type, " with a prompt"), value: customPrompt, onChange: handlePromptChange, onKeyDown: handleKeyDown, 
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus: autoFocusPrompt }), _jsxs("div", { className: styles['assistant-label'], children: [_jsx(Icon, { glyph: GLYPHS.aiIcon, size: "small" }), _jsx("span", { children: "Squire AI Assistant" })] })] }), _jsx("button", { className: classNames(styles['submit-prompt-button'], (_c = {},
                                _c[styles['is-focused']] = !!customPrompt,
                                _c)), onClick: handlePromptSubmit, type: "button", children: _jsx(Icon, { glyph: GLYPHS.arrowUp }) })] })) })] }));
};
